import {
  CountryResponse,
  LayoutResponse,
  PlanResponse,
  StateResponse,
  TemplateType,
  LanguageEnabled,
} from '@/api/tax-guide';
import { NotFoundComponent } from '@/ui/templates/NotFound/NotFoundComponent';
import { Box, Stack } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import CustomBanner from './AtomicComponents/CustomBanner';
import { DirectLoginStateOption } from '@/commonUtilities/fontUtils';
import { TaxGuideContext } from '@/state/TaxGuideProvider';
import { ModalContext } from '@/state/ModalProvider';
import { TaxAdvantage } from './AtomicComponents/TaxAdvantage';
import {
  CountryStateDropDown,
  PlanDropdown,
  LanguageDropdown,
} from './DropdownComponents';
import DownloadButton from './AtomicComponents/DownloadButton';

export type InputDataProps = {
  layoutData: LayoutResponse;
  template: TemplateType;
  setBottomBanner: (value: boolean) => void;
};

export type Plan = PlanResponse & { disabled?: boolean };

const InputSection = (props: InputDataProps) => {
  const { layoutData, template, setBottomBanner } = props;

  const defaultCountryCode = layoutData.userCountryCode;

  let countryValue = undefined;
  let planValue = undefined;
  hydrateContentPage();

  function hydrateContentPage() {
    if (
      defaultCountryCode &&
      layoutData.countries.filter(
        country => country.id === defaultCountryCode.toLowerCase(),
      ).length > 0
    ) {
      countryValue = layoutData.countries.filter(
        country => country.id === defaultCountryCode.toLowerCase(),
      )[0];
    } else if (!defaultCountryCode && layoutData.plans.length === 1) {
      countryValue = layoutData.countries[0];
    }
    if (layoutData.plans.length === 1) {
      planValue = layoutData.plans[0];
    }
  }

  const [country, setCountry] = useState<CountryResponse | undefined>(
    countryValue,
  );
  const [state, setState] = useState<StateResponse | undefined>();

  const [planList, setPlanList] = useState<Plan[] | undefined>(
    layoutData.plans,
  );
  const [plan, setPlan] = useState<Plan | undefined>(planValue);

  const modalStatus = useContext(ModalContext);
  const showStateDD =
    layoutData.folderName &&
    template === 'custom' &&
    DirectLoginStateOption.filter(value => value === layoutData.folderName)
      .length > 0;
  const {
    isPending,
    content,
    updateRequest,
    isFetching,
    isDownloading,
    downloadPDFCall,
  } = useContext(TaxGuideContext);
  if (isPending || isFetching) modalStatus.setModal(false);

  useEffect(() => {
    if (layoutData.plans.length > 1) {
      if (
        !country?.eligiblePlans?.filter(
          eligibleplan => eligibleplan === plan?.id,
        )[0]
      ) {
        setPlan(undefined);
      }

      const eligiblePlanList =
        planList &&
        planList.map(plan => {
          plan.disabled = true;
          country?.eligiblePlans?.forEach(y => {
            if (plan.id === y) {
              plan.disabled = false;
            }
          });
          return plan;
        });
      setPlanList(eligiblePlanList);
    }
    setState(undefined);
  }, [country]);

  useEffect(() => {
    updateRequest({
      planId: plan?.id ?? '',
      countryId: country?.id ?? '',
      stateId: state?.id ?? '',
      taxAdvantage: null,
      taxCalculationPopupList: null,
    });
  }, [country, plan, state]);

  useEffect(() => {
    if (!country || !plan) setBottomBanner(true);
    else setBottomBanner(false);
  }, [plan, country]);

  if (!layoutData.countries) return <NotFoundComponent />;

  return (
    <>
      <Stack
        direction={{ xs: 'column', sm: 'column', md: 'column', lg: 'row' }}
        sx={{
          alignItems: 'center',
          padding: 2,
        }}
      >
        <Stack
          direction={
            layoutData.layout.headerHtml
              ? {
                  xs: 'column',
                }
              : { xs: 'column', sm: 'row' }
          }
          sx={{ flex: 1 }}
        >
          <Stack direction={{ xs: 'column' }}>
            <CountryStateDropDown
              selectedCountry={country}
              countryList={layoutData.countries}
              setCountry={setCountry}
              showState={Boolean(
                country?.states && (template !== 'custom' || showStateDD),
              )}
              selectedState={state}
              setState={setState}
            />

            <LanguageDropdown
              languages={layoutData.languages}
              enabled={LanguageEnabled}
            />
          </Stack>
          {
            <PlanDropdown
              planList={planList ? planList : []}
              plan={plan}
              setPlan={setPlan}
              enabled={planList && planList.length > 1}
            />
          }
          <TaxAdvantage
            layoutType={
              layoutData.layout.headerHtml
                ? 'withSubHeader'
                : 'withoutSubHeader'
            }
            taxAdvResp={content?.data.taxAdvantage}
            onChange={toggleValue => {
              if (content?.data.taxAdvantage) {
                updateRequest({
                  taxAdvantage: {
                    displayToggle: toggleValue,
                    approvedConditionsId:
                      content.data.taxAdvantage.approvedConditionsId,
                  },
                });
              }
            }}
          />

          <Box
            sx={
              layoutData.layout.headerHtml
                ? {
                    marginLeft: '80px',
                    paddingTop: 0.5,
                    marginBottom: 1,
                  }
                : {
                    paddingTop: 0.5,
                    marginLeft: 'auto',
                  }
            }
          >
            <DownloadButton
              isDownloading={isDownloading}
              content={content}
              downloadCall={downloadPDFCall}
              buttonText={'Download PDF'}
            />
          </Box>
        </Stack>
        <CustomBanner htmlString={layoutData.layout.headerHtml} />
      </Stack>
    </>
  );
};

export default InputSection;
